import Icon from '../common/Icon.tsx'
import { formatMillisToMainTime } from '@/utils/formatters.ts'
import { useTranslation } from 'react-i18next'
import Button from '../common/Button.tsx'
import useAudioStore from '../../shared/store/audio.store.tsx'
import React, { useCallback, useEffect } from 'react'
import { mixTracks } from '@/utils/mix-tracks.ts'
import useTracksStore, { MIX_ID } from '../../shared/store/tracks.store.tsx'
import { useShallow } from 'zustand/react/shallow'
import HistoryControls from './HistoryControls.tsx'
import useMediaStore from '../../shared/store/media.store.tsx'
import { useTracking } from '@/providers/TrackingProvider.tsx'

const MainControls: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const { trackEvent } = useTracking()
  const { endTime, playbackTime, setPlaybackTime } = useTracksStore(
    useShallow((state) => ({
      endTime: state.endTime,
      playbackTime: state.playbackTime,
      setPlaybackTime: state.setPlaybackTime,
    }))
  )

  const {
    playAudioBuffer,
    pausePlayback,
    setProgress,
    currentAudioId,
    progress,
    isPaused,
    duration,
    isPlaying,
    audioContext,
  } = useAudioStore(
    useShallow((state) => ({
      playAudioBuffer: state.playAudioBuffer,
      pausePlayback: state.pausePlayback,
      setProgress: state.setProgress,
      currentAudioId: state.currentAudioId,
      progress: state.progress,
      isPaused: state.isPaused,
      duration: state.duration,
      isPlaying: state.isPlaying,
      audioContext: state.audioContext,
    }))
  )
  const { t } = useTranslation()

  useEffect(() => {
    if (currentAudioId === MIX_ID) {
      setPlaybackTime(progress * duration)
    }
  }, [progress, duration, currentAudioId])

  useEffect(() => {
    const isTargetEditable = (elm: HTMLElement) => {
      return elm.tagName === 'INPUT' || elm.tagName === 'TEXTAREA'
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTargetEditable(event.target as HTMLElement)) return
      if (event.code !== 'Space') return
      event.preventDefault()

      if (isPlaying && currentAudioId === MIX_ID) {
        pausePlayback()
      } else {
        void onPlay()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isPlaying, pausePlayback, currentAudioId])

  const onPlay = useCallback(async () => {
    const tracksStore = useTracksStore.getState()
    trackEvent('ProEditor - Previewed audio - MainControls', {
      trackCount: tracksStore.tracks.length,
      maxProjectDuration: tracksStore.maxProjectDuration,
      playbackTime: tracksStore.playbackTime,
    })
    const { media } = useMediaStore.getState()
    const mix = await mixTracks(
      tracksStore.tracks,
      media,
      audioContext,
      tracksStore.maxProjectDuration
    )
    void playAudioBuffer(
      MIX_ID,
      mix,
      mix.duration ? tracksStore.playbackTime / mix.duration : 0
    )
  }, [audioContext, playAudioBuffer, trackEvent])

  const updatePlaybackTime = useCallback(
    (newTime: number) => {
      if (currentAudioId === MIX_ID) {
        setProgress(newTime / duration)
      } else {
        void setPlaybackTime(newTime)
      }
    },
    [currentAudioId, duration, setProgress]
  )

  const renderMainClock = useCallback(() => {
    return (
      <div className="flex items-center justify-start text-lg tabular-nums text-emerald-400">
        {formatMillisToMainTime(playbackTime * 1000)}
      </div>
    )
  }, [playbackTime])

  return (
    <div className="flex items-center justify-center gap-4">
      <div className="inline-flex h-16 items-center justify-center gap-6 rounded-lg border-2 border-zinc-900 bg-black px-[18px] py-2">
        <div
          className="flex items-center justify-start gap-1"
          data-testid="current-time"
        >
          <div className="text-xs font-normal leading-none text-white">
            {t('ls_main_controls_current')}
          </div>
          <div className="flex items-center justify-start text-lg tabular-nums text-emerald-400">
            {renderMainClock()}
          </div>
        </div>
        <div className="flex flex-col items-start justify-center text-xs">
          <div className="flex items-center justify-start gap-1">
            <span className="flex h-4 min-w-11 items-center gap-2 text-white">
              {t('ls_main_controls_start')}
            </span>
            <span
              className="flex h-4 items-center tabular-nums text-emerald-400"
              data-testid="start-time"
            >
              {formatMillisToMainTime(0)}
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-1">
            <span className="h-4 min-w-11 items-center gap-2 text-white">
              {t('ls_main_controls_end')}
            </span>
            <span
              className="flex h-4 items-center tabular-nums text-emerald-400"
              data-testid="end-time"
            >
              {formatMillisToMainTime(endTime * 1000)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <div className="flex w-full justify-between gap-[8px]">
          <Button
            squared
            onClick={() => updatePlaybackTime(0)}
            disabled={isLoading}
          >
            <Icon name="previous" />
          </Button>
          {currentAudioId === MIX_ID && isPlaying && !isPaused ? (
            <Button squared onClick={pausePlayback} disabled={isLoading}>
              <Icon name="pause" />
            </Button>
          ) : (
            <Button squared onClick={onPlay} disabled={isLoading}>
              <Icon name="play" />
            </Button>
          )}
          <Button
            squared
            onClick={() => updatePlaybackTime(endTime)}
            disabled={isLoading}
          >
            <Icon name="next" />
          </Button>
        </div>
        <HistoryControls isLoading={isLoading} />
      </div>
    </div>
  )
}

export default MainControls
